import React, { useState, useMemo, useEffect } from 'react'
import { Search, Phone, Mail, MapPin, Clock, CheckCircle, AlertTriangle, Users, Calendar } from 'lucide-react'
import { DetailedSupplier } from '../../types/suppliers'
import { useSuppliers, useSupplierSearch } from '../../hooks/useSuppliers'
import { SupplierService } from '../../services/supplierService'

interface SuppliersDirectoryProps {
  suppliers?: DetailedSupplier[]
}

export const SuppliersDirectory: React.FC<SuppliersDirectoryProps> = ({
  suppliers: propSuppliers
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState<DetailedSupplier | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | DetailedSupplier['status']>('all')

  // Fetch suppliers from API
  const { data: apiSuppliers, isLoading, error } = useSuppliers(
    undefined,
    statusFilter === 'all' ? undefined : statusFilter,
    500
  )

  // Use search API when search term is provided
  const { data: searchData } = useSupplierSearch(
    searchTerm,
    undefined
  )

  // Transform API suppliers to DetailedSupplier format
  const suppliers = useMemo(() => {
    if (propSuppliers) {
      return propSuppliers
    }
    
    if (searchTerm && searchData) {
      return searchData.suppliers.map(s => SupplierService.transformToDetailedSupplier(s))
    }
    
    if (apiSuppliers) {
      return apiSuppliers.map(s => SupplierService.transformToDetailedSupplier(s))
    }
    
    return []
  }, [propSuppliers, apiSuppliers, searchData, searchTerm])

  // Filter and search suppliers
  const filteredSuppliers = useMemo(() => {
    let filtered = suppliers

    // Apply status filter (already filtered in API call, but need to filter for propSuppliers)
    if (propSuppliers && statusFilter !== 'all') {
      filtered = filtered.filter(supplier => supplier.status === statusFilter)
    }

    // Apply local search if propSuppliers are provided
    if (propSuppliers && searchTerm.trim()) {
      filtered = SupplierService.searchSuppliers(filtered, searchTerm)
    }

    return filtered
  }, [suppliers, searchTerm, statusFilter, propSuppliers])

  // Status counts for filter badges
  const statusCounts = useMemo(() => {
    return {
      all: suppliers.length,
      on_site: suppliers.filter(s => s.status === 'on_site').length,
      en_route: suppliers.filter(s => s.status === 'en_route').length,
      confirmed: suppliers.filter(s => s.status === 'confirmed').length,
      delayed: suppliers.filter(s => s.status === 'delayed').length
    }
  }, [suppliers])

  const getStatusIcon = (status: DetailedSupplier['status']) => {
    switch (status) {
      case 'on_site': return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'en_route': return <Clock className="w-4 h-4 text-blue-600" />
      case 'delayed': return <AlertTriangle className="w-4 h-4 text-red-600" />
      case 'confirmed': return <Calendar className="w-4 h-4 text-gray-600" />
      default: return null
    }
  }

  const getStatusColor = (status: DetailedSupplier['status']) => {
    switch (status) {
      case 'on_site': return 'text-green-600 bg-green-50 border-green-200'
      case 'en_route': return 'text-blue-600 bg-blue-50 border-blue-200'  
      case 'delayed': return 'text-red-600 bg-red-50 border-red-200'
      case 'confirmed': return 'text-gray-600 bg-gray-50 border-gray-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'text-green-600 bg-green-50'
      case 'in_progress': return 'text-blue-600 bg-blue-50'
      case 'pending': return 'text-orange-600 bg-orange-50'
      case 'delayed': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const formatTime = (date: Date): string => {
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const getPrimaryContact = (supplier: DetailedSupplier) => {
    return supplier.contacts.find(c => c.id === supplier.primaryContactId) || supplier.contacts[0]
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 600, margin: '0 0 8px 0', color: '#1a1a1a' }}>
          Supplier Directory
        </h1>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
          Manage contacts, track status, and coordinate with your event suppliers
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: '24px' }}>
        {/* Search Bar */}
        <div style={{ 
          position: 'relative', 
          marginBottom: '16px',
          maxWidth: '400px'
        }}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search suppliers, contacts, or roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 10px 10px 40px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4285f4'}
            onBlur={(e) => e.target.style.borderColor = '#ddd'}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                fontSize: '18px',
                color: '#666',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Status Filter Pills */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All Suppliers', count: statusCounts.all },
            { key: 'on_site', label: 'On Site', count: statusCounts.on_site },
            { key: 'en_route', label: 'En Route', count: statusCounts.en_route },
            { key: 'confirmed', label: 'Confirmed', count: statusCounts.confirmed },
            { key: 'delayed', label: 'Delayed', count: statusCounts.delayed }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setStatusFilter(filter.key as any)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '20px',
                border: statusFilter === filter.key ? '2px solid #4285f4' : '1px solid #ddd',
                background: statusFilter === filter.key ? '#e3f2fd' : 'white',
                color: statusFilter === filter.key ? '#1976d2' : '#666',
                fontSize: '13px',
                fontWeight: statusFilter === filter.key ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {filter.key !== 'all' && getStatusIcon(filter.key as any)}
              <span>{filter.label}</span>
              <span style={{
                background: statusFilter === filter.key ? '#1976d2' : '#e0e0e0',
                color: statusFilter === filter.key ? 'white' : '#666',
                borderRadius: '10px',
                padding: '1px 6px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {filter.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Suppliers List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {filteredSuppliers.map(supplier => {
          const primaryContact = getPrimaryContact(supplier)
          const nextTask = supplier.currentTasks.find(t => t.status === 'pending' || t.status === 'in_progress')
          const isExpanded = selectedSupplier?.id === supplier.id
          
          return (
            <div key={supplier.id} style={{ marginBottom: '4px' }}>
              {/* Horizontal Supplier Card */}
              <div
                onClick={() => setSelectedSupplier(isExpanded ? null : supplier)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'white',
                  borderRadius: isExpanded ? '12px 12px 0 0' : '12px',
                  border: '1px solid #e0e0e0',
                  borderBottom: isExpanded ? 'none' : '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'
                  }
                  e.currentTarget.style.borderColor = '#4285f4'
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'
                  }
                  e.currentTarget.style.borderColor = isExpanded ? '#4285f4' : '#e0e0e0'
                }}
              >
                {/* Left: Supplier Info */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ minWidth: '120px' }}>
                    <h3 style={{ 
                      fontSize: '16px', 
                      fontWeight: 600, 
                      margin: '0 0 4px 0',
                      color: '#1a1a1a' 
                    }}>
                      {supplier.name}
                    </h3>
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#666', 
                      margin: 0 
                    }}>
                      {supplier.role}
                    </p>
                  </div>

                  {/* Primary Contact */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '200px' }}>
                    <img 
                      src={primaryContact.photo}
                      alt={primaryContact.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                        {primaryContact.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {primaryContact.role}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <a 
                      href={`tel:${primaryContact.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        background: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        textDecoration: 'none',
                        color: '#666',
                        fontSize: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e3f2fd'
                        e.currentTarget.style.color = '#1976d2'
                        e.currentTarget.style.borderColor = '#1976d2'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f8f9fa'
                        e.currentTarget.style.color = '#666'
                        e.currentTarget.style.borderColor = '#e0e0e0'
                      }}
                    >
                      <Phone className="w-3 h-3" />
                      Call
                    </a>
                    <a 
                      href={`mailto:${primaryContact.email}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        background: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        textDecoration: 'none',
                        color: '#666',
                        fontSize: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e3f2fd'
                        e.currentTarget.style.color = '#1976d2'
                        e.currentTarget.style.borderColor = '#1976d2'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f8f9fa'
                        e.currentTarget.style.color = '#666'
                        e.currentTarget.style.borderColor = '#e0e0e0'
                      }}
                    >
                      <Mail className="w-3 h-3" />
                      Email
                    </a>
                  </div>
                </div>

                {/* Right: Status & Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Quick Stats */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 500, color: '#333' }}>{supplier.teamSize || 'N/A'}</div>
                      <div>Team</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 500, color: '#333' }}>{supplier.totalEventsWithVenue}</div>
                      <div>Events</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        fontWeight: 500,
                        color: supplier.compliance.overallStatus === 'compliant' ? '#4caf50' : 
                               supplier.compliance.overallStatus === 'pending' ? '#ff9800' : '#f44336'
                      }}>
                        {supplier.compliance.overallStatus === 'compliant' ? '✓' :
                         supplier.compliance.overallStatus === 'pending' ? '⏳' : '❌'}
                      </div>
                      <div>Docs</div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    border: '1px solid',
                    fontSize: '13px',
                    fontWeight: 500,
                    minWidth: '100px',
                    justifyContent: 'center'
                  }} className={getStatusColor(supplier.status)}>
                    {getStatusIcon(supplier.status)}
                    {supplier.status.replace('_', ' ').toUpperCase()}
                  </div>

                  {/* Expand Arrow */}
                  <div style={{ 
                    fontSize: '18px', 
                    color: '#666',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}>
                    ▼
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={{
                  background: '#f8f9fa',
                  border: '1px solid #4285f4',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  padding: '20px',
                  transition: 'all 0.3s ease-in-out'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    {/* All Contacts */}
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 12px 0', color: '#1a1a1a' }}>
                        Team Contacts
                      </h4>
                      {supplier.contacts.map(contact => (
                        <div key={contact.id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '8px',
                          padding: '8px',
                          background: 'white',
                          borderRadius: '6px',
                          border: contact.id === supplier.primaryContactId ? '1px solid #1976d2' : '1px solid #e0e0e0'
                        }}>
                          <img 
                            src={contact.photo}
                            alt={contact.name}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                              {contact.name}
                              {contact.id === supplier.primaryContactId && (
                                <span style={{ fontSize: '10px', marginLeft: '4px', color: '#1976d2' }}>PRIMARY</span>
                              )}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{contact.role}</div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{contact.phone}</div>
                          </div>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: contact.availability === 'available' ? '#4caf50' :
                                       contact.availability === 'on-call' ? '#ff9800' : '#f44336'
                          }} />
                        </div>
                      ))}
                    </div>

                    {/* Current Tasks */}
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 12px 0', color: '#1a1a1a' }}>
                        Today's Tasks
                      </h4>
                      {supplier.currentTasks.map(task => (
                        <div key={task.id} style={{ 
                          marginBottom: '8px',
                          padding: '8px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '4px' }}>
                            {task.name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {formatTime(task.startTime)} - {formatTime(task.endTime)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {task.assignedContact} • {task.location}
                          </div>
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            marginTop: '4px',
                            display: 'inline-block'
                          }} className={getTaskStatusColor(task.status)}>
                            {task.status.toUpperCase()}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Equipment & Setup */}
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 12px 0', color: '#1a1a1a' }}>
                        Setup Details
                      </h4>
                      <div style={{ 
                        padding: '8px',
                        background: 'white',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        marginBottom: '8px'
                      }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Equipment</div>
                        {supplier.equipment?.map((item, index) => (
                          <div key={index} style={{ fontSize: '11px', color: '#333' }}>• {item}</div>
                        ))}
                      </div>
                      
                      {supplier.vehicleInfo && (
                        <div style={{ 
                          padding: '8px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0',
                          marginBottom: '8px'
                        }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Vehicle</div>
                          <div style={{ fontSize: '11px', color: '#333' }}>
                            {supplier.vehicleInfo.type} ({supplier.vehicleInfo.licensePlate})
                          </div>
                          {supplier.vehicleInfo.parkingAssigned && (
                            <div style={{ fontSize: '11px', color: '#333' }}>
                              Parking: {supplier.vehicleInfo.parkingAssigned}
                            </div>
                          )}
                        </div>
                      )}

                      {supplier.specialInstructions && (
                        <div style={{ 
                          padding: '8px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Special Instructions</div>
                          <div style={{ fontSize: '11px', color: '#333' }}>{supplier.specialInstructions}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Loading State */}
      {isLoading && !propSuppliers && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
            Loading suppliers...
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !propSuppliers && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#d32f2f',
          background: '#ffebee',
          borderRadius: '12px',
          border: '1px solid #ffcdd2'
        }}>
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: '#d32f2f' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
            Error loading suppliers
          </h3>
          <p style={{ fontSize: '14px' }}>
            {error.message || 'Failed to load suppliers from the server'}
          </p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredSuppliers.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e0e0'
        }}>
          <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
            No suppliers found
          </h3>
          <p style={{ fontSize: '14px' }}>
            {searchTerm 
              ? `No suppliers match "${searchTerm}"`
              : 'No suppliers in this category'
            }
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#4285f4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Clear search
            </button>
          )}
        </div>
      )}

    </div>
  )
}

export default SuppliersDirectory